import { createEffect, onCleanup, onMount, Show, type ParentProps } from "solid-js"
import { createStore } from "solid-js/store"
import { Logo } from "@opencode/ui/logo"
import { useLanguage } from "@/runtime/i18n/language"
import { usePlatform } from "@/runtime/platform/platform"
import { SignInAtmosphere } from "./sign-in-atmosphere"
import { SairEntryContext, useSairEntry, type SairEntryPhase } from "./entry-context"
import "./entry.css"

// Presentation only: the desktop and account gate still own their readiness and permissions.
// Keeping this above AppInterface lets the same geometry survive its initial mount.
export function SairEntryProvider(props: ParentProps<{ startupReady?: boolean }>) {
  const language = useLanguage()
  const platform = usePlatform()
  const [state, setState] = createStore({
    reported: undefined as SairEntryPhase | undefined,
    phase: "checking" as Exclude<SairEntryPhase, "ready">,
    hidden: props.startupReady === undefined,
    leaving: false,
    reduced: false,
    visibleStatus: false,
    slow: false,
    panel: undefined as HTMLDivElement | undefined,
  })
  const ready = () => state.reported === "ready" && (props.startupReady ?? true)
  const finish = () => {
    if (ready()) setState({ hidden: true, leaving: false })
  }

  onMount(() => {
    const motion = matchMedia("(prefers-reduced-motion: reduce)")
    const sync = () => setState("reduced", motion.matches)
    sync()
    motion.addEventListener("change", sync)
    onCleanup(() => motion.removeEventListener("change", sync))
  })

  createEffect(() => {
    if (state.reported === undefined && props.startupReady === undefined) {
      setState("hidden", true)
      return
    }
    if (!ready()) {
      setState({ hidden: false, leaving: false })
      if (state.reported && state.reported !== "ready") setState("phase", state.reported)
      return
    }
    if (state.hidden) return
    if (state.reduced) {
      finish()
      return
    }
    setState("leaving", true)
    // A hidden window may not dispatch transitionend. This never delays app readiness.
    const timer = setTimeout(finish, 280)
    onCleanup(() => clearTimeout(timer))
  })

  createEffect(() => {
    if (state.phase !== "checking" || state.hidden || state.leaving) {
      setState({ visibleStatus: false, slow: false })
      return
    }
    const visible = setTimeout(() => setState("visibleStatus", true), 250)
    const slow = setTimeout(() => setState("slow", true), 4000)
    onCleanup(() => {
      clearTimeout(visible)
      clearTimeout(slow)
    })
  })

  return (
    <SairEntryContext.Provider value={{ panel: () => state.panel, report: (phase) => setState("reported", phase) }}>
      <div class="sair-entry-app" inert={!state.hidden && !state.leaving} aria-hidden={!state.hidden && !state.leaving}>
        {props.children}
      </div>
      <main
        data-component="sair-entry"
        data-testid={state.hidden ? undefined : "sair-login"}
        data-presentation={state.phase}
        data-state={state.hidden ? "hidden" : state.leaving ? "leaving" : "visible"}
        hidden={state.hidden}
        class="sair-entry"
        classList={{ "sair-login-desktop": platform.platform === "desktop" }}
        aria-busy={state.phase === "checking" && !state.leaving}
        onTransitionEnd={(event) => {
          if (event.target === event.currentTarget && event.propertyName === "opacity") finish()
        }}
      >
        <div class="sair-entry-scroll">
          <div class="sair-entry-stage">
            <aside class="sair-entry-art" aria-hidden="true">
              <SignInAtmosphere
                loading={state.phase === "checking"}
                active={!state.hidden && !state.leaving}
                subdued={state.phase === "pending"}
              />
            </aside>
            <div class="sair-entry-status" aria-live="polite">
              <Show when={state.phase === "checking" && state.visibleStatus}>
                <h1>{language.t("sair.opening.title")}</h1>
                <Show when={state.slow}>
                  <p>{language.t("sair.check.slow")}</p>
                </Show>
              </Show>
            </div>
            <div
              class="sair-entry-panel"
              inert={state.phase === "checking" || state.hidden || state.leaving}
              aria-hidden={state.phase === "checking" || state.hidden || state.leaving}
            >
              <div ref={(element) => setState("panel", element)} />
              <Show when={state.reported === "ready" && state.phase !== "checking" && !state.hidden && !state.leaving}>
                <section class="sair-entry-preparing" role="status">
                  <Logo class="sair-entry-wordmark" aria-label={language.t("sair.logo")} />
                  <p>{language.t("sair.workspace.preparing")}</p>
                </section>
              </Show>
            </div>
          </div>
        </div>
      </main>
    </SairEntryContext.Provider>
  )
}

export function SairEntryConnection(props: ParentProps) {
  const entry = useSairEntry()
  onMount(() => entry.report("ready"))
  return <>{props.children}</>
}
